import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';

import type { TenantRoleViewModel } from '../api';

export interface TenantRoleFormInput {
  readonly name: string;
  readonly description: string;
}

interface FieldErrors {
  name?: string;
}

/**
 * Create/edit dialog for a tenant's role catalogue entry (PTV2-1426, `/tenants/{uuid}/roles`). The
 * backend only mutates `description` on update (`RoleServiceImpl.updateDescription` ignores `name`),
 * so the name field is locked once a role exists — matches the server-enforced invariant instead of
 * silently accepting an edit that would be dropped.
 */
export function TenantRoleFormDialog({
  open,
  initial,
  onOpenChange,
  onSubmit,
  pending,
  submitError,
}: {
  readonly open: boolean;
  /** Non-null = edit; null = create. */
  readonly initial: TenantRoleViewModel | null;
  readonly onOpenChange: (next: boolean) => void;
  readonly onSubmit: (input: TenantRoleFormInput) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const isEdit = initial !== null;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});

  useEffect(() => {
    if (open) {
      setName(initial?.name ?? '');
      setDescription(initial?.description ?? '');
      setErrors({});
    }
  }, [open, initial]);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!isEdit && trimmedName.length === 0) {
      setErrors({ name: t('routes.platform.detail.roles.form.errors.nameRequired') });
      return;
    }
    setErrors({});
    onSubmit({ name: trimmedName, description: description.trim() });
  }

  const submitErrorCode = getErrorCodeForUi(submitError);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent disableOutsideClose>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('routes.platform.detail.roles.form.editTitle') : t('routes.platform.detail.roles.form.createTitle')}
          </DialogTitle>
          {isEdit && (
            <DialogDescription>{t('routes.platform.detail.roles.form.editHint')}</DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <FormField
            id="tenant-role-name"
            label={t('routes.platform.detail.roles.form.fields.name')}
            required={!isEdit}
            error={errors.name}
          >
            {(fp) => (
              <Input
                {...fp}
                value={name}
                onChange={(e) => { setName(e.target.value); }}
                disabled={isEdit}
                autoComplete="off"
              />
            )}
          </FormField>
          <FormField
            id="tenant-role-description"
            label={t('routes.platform.detail.roles.form.fields.description')}
          >
            {(fp) => (
              <Input
                {...fp}
                value={description}
                onChange={(e) => { setDescription(e.target.value); }}
                autoComplete="off"
              />
            )}
          </FormField>
          {submitError !== undefined && submitError !== null && (
            <p role="alert" className="text-sm text-red-600">
              {t('routes.platform.detail.roles.form.errors.submit', { code: submitErrorCode })}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => { onOpenChange(false); }}
              disabled={pending}
            >
              {t('routes.platform.detail.roles.form.cancel')}
            </Button>
            <Button type="submit" loading={pending}>
              {isEdit ? t('routes.platform.detail.roles.form.save') : t('routes.platform.detail.roles.form.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
